import {
  Box,
  HStack,
  Flex,
  Text,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankLoanAccountWithCollateralStats,
  parse,
} from '@finley-technologies/valley-bank-utils';

import {
  CommentFragmentFragmentGQLType,
  useGetValleyBankLatestPinnedCommentForLoanAccountsLazyQueryGQLType,
  useGetValleyBankUnderMarginLoanAccountsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  SingleEntityDrawer as ValleyBankLoanAccountEventLogSingleEntityDrawer,
  MultiEntityDrawer as ValleyBankLoanAccountEventLogMultiEntityDrawer,
} from '~/core/routes/valley-bank/EntityEventLogDrawers';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { RenderStaticHtml } from '~/packages/common-frontend/components/RichTextEditor/RichTextEditor';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';
import {
  getColumnNames,
  createDefaultTableColumnConfiguration,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import { formatCurrencyAmount } from '~/packages/common-frontend/utils';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { CustomerTag } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { LoanAccountTag } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoanAccountsOverviewTemplate } from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsOverviewTemplate';
import {
  LoanAccountsTableContainer,
  loanAccountsColumnDefs,
} from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsTable';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { DashboardMode } from '~/valleyBank/domains/page/hooks/DataMetadataContextType';

const TABLE_KEY_NON_PREVIEW: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountsUnderMarginNonPreview';

const TABLE_KEY_PREVIEW: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountsUnderMarginPreview';

const DEFAULT_TABLE_CONFIG_NON_PREVIEW = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_NON_PREVIEW,
  columns: [
    { columnKey: 'loanAccountNumber', isVisible: true, isFixed: false },
    { columnKey: 'pinnedComment', isVisible: true, isFixed: false },
    { columnKey: 'borrower', isVisible: true, isFixed: false },
    { columnKey: 'outstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'assignedMarketValue', isVisible: true, isFixed: false },
    { columnKey: 'effectiveAdvanceRate', isVisible: true, isFixed: false },
    {
      columnKey: 'assignedPreAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocationRate',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'assignedPostAllocationLendableValue',
      isVisible: false,
      isFixed: false,
    },
    { columnKey: 'marginAvailability', isVisible: true, isFixed: false },
    { columnKey: 'marginAvailabilityRate', isVisible: false, isFixed: false },
    { columnKey: 'arrangementGroupId', isVisible: true, isFixed: false },
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'numberOfLoanParties', isVisible: true, isFixed: false },
  ],
});

const DEFAULT_TABLE_CONFIG_PREVIEW = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_PREVIEW,
  columns: [
    { columnKey: 'loanAccountNumber', isVisible: true, isFixed: false },
    { columnKey: 'borrower', isVisible: true, isFixed: false },
    { columnKey: 'outstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'assignedMarketValue', isVisible: true, isFixed: false },
    { columnKey: 'effectiveAdvanceRate', isVisible: true, isFixed: false },
    {
      columnKey: 'assignedPreAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocationRate',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'assignedPostAllocationLendableValue',
      isVisible: false,
      isFixed: false,
    },
    { columnKey: 'marginAvailability', isVisible: true, isFixed: false },
    { columnKey: 'marginAvailabilityRate', isVisible: false, isFixed: false },
    { columnKey: 'arrangementGroupId', isVisible: true, isFixed: false },
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'numberOfLoanParties', isVisible: true, isFixed: false },
  ],
});

type GeneratePinnedCommentColumnDefInput = {
  pinnedCommentMap: any;
};

type AllUndefined<T> = {
  [K in keyof T]: undefined;
};

type PinnedCommentCellProps = {
  accountName: string | null;
  cifNum: string;
  dashboardMode: Exclude<DashboardMode, 'preview'>;
  loanAccountNumber: string;
  pinnedCommentHtml?: string;
  handleDrawerClose: () => void;
};
const PinnedCommentCell = ({
  accountName,
  cifNum,
  dashboardMode,
  loanAccountNumber,
  pinnedCommentHtml,
  handleDrawerClose,
}: PinnedCommentCellProps) => {
  // Refetches pinned comments on drawer close
  const disclosure = useDisclosure({ onClose: handleDrawerClose });
  return (
    <Flex gap="1rem" alignItems="center" p="1rem 0">
      {dashboardMode === 'current' && (
        <ValleyBankLoanAccountEventLogSingleEntityDrawer
          disclosure={disclosure}
          entityId={loanAccountNumber}
          header={
            <Flex alignItems="center" gap="1rem">
              <Typography variant="section-title" trim>
                Updates
              </Typography>

              <Flex
                fontSize="xs"
                gap="1rem"
                alignItems="baseline"
                paddingRight="1.5rem"
              >
                <LoanAccountTag loanAccountId={loanAccountNumber} />
                <CustomerTag
                  cifnum={cifNum}
                  label={`Borrower: ${accountName} (${cifNum})`}
                />
              </Flex>
            </Flex>
          }
        />
      )}
      <Box flexGrow={1}>
        {pinnedCommentHtml && pinnedCommentHtml.length > 0 ? (
          <RenderStaticHtml textHtml={pinnedCommentHtml} color="gray.600" />
        ) : (
          <Text>-</Text>
        )}
      </Box>
    </Flex>
  );
};

const generatePinnedCommentColumnDef = ({
  pinnedCommentMap,
  dashboardMode,
  handleDrawerClose,
}: GeneratePinnedCommentColumnDefInput & {
  dashboardMode: Exclude<DashboardMode, 'preview'>;
  handleDrawerClose: () => void;
}): ColumnDef<ValleyBankLoanAccountWithCollateralStats, any> => ({
  id: 'pinnedComment',
  header: 'Comment',
  enableSorting: false,
  enableColumnFilter: false,
  // eslint-disable-next-line react/display-name
  cell: ({ row }) => {
    const { loanAccountNumber, cifNum, accountName } = row.original;
    const pinnedCommentHtml =
      pinnedCommentMap && pinnedCommentMap[loanAccountNumber]
        ? pinnedCommentMap[loanAccountNumber].pinnedComment.comment
        : undefined;

    return (
      <PinnedCommentCell
        accountName={accountName}
        cifNum={cifNum}
        dashboardMode={dashboardMode}
        loanAccountNumber={loanAccountNumber}
        handleDrawerClose={handleDrawerClose}
        pinnedCommentHtml={pinnedCommentHtml}
      />
    );
  },
  meta: {
    style: {
      padding: '0px',
      textAlign: 'left',
      minWidth: '12em',
    },
  },
});

const generateColumnDefs = (
  input:
    | (AllUndefined<GeneratePinnedCommentColumnDefInput> & {
        dashboardMode: Extract<DashboardMode, 'preview'>;
        handleDrawerClose: () => void;
      })
    | (GeneratePinnedCommentColumnDefInput & {
        dashboardMode: Exclude<DashboardMode, 'preview'>;
        handleDrawerClose: () => void;
      }),
): ColumnDef<ValleyBankLoanAccountWithCollateralStats, any>[] => {
  if (input.dashboardMode === 'preview') {
    return loanAccountsColumnDefs;
  }
  return [
    loanAccountsColumnDefs[0],
    generatePinnedCommentColumnDef(input),
    ...loanAccountsColumnDefs.slice(1),
  ];
};

export const getUnderMarginReportDataForDownload = ({
  tableInstance,
  pinnedCommentMap,
  columnIdsToExclude = [],
}: {
  tableInstance: Table<any>;
  pinnedCommentMap?: {
    [key: string]: {
      loanAccountNum: string;
      pinnedComment: CommentFragmentFragmentGQLType;
    };
  };
  columnIdsToExclude?: string[];
}) => {
  const dataRows = _.map(tableInstance.getFilteredRowModel().rows, (row) => {
    const dataRow: { [key: string]: any } = {};

    // logic to get pinned comment if it exists
    const cellsByColumnId = row._getAllCellsByColumnId();
    const loanAccountNumber = String(
      cellsByColumnId.loanAccountNumber?.getValue() ?? '',
    );
    const pinnedComment = pinnedCommentMap
      ? pinnedCommentMap[loanAccountNumber]?.pinnedComment.comment
      : '-';

    _.forEach(row.getVisibleCells(), (cell) => {
      const key = cell.column.columnDef.header?.toString() ?? cell.column.id;
      if (cell.column.id === 'pinnedComment') {
        dataRow[key] = pinnedComment;
      } else if (
        cell.column.id === undefined ||
        !columnIdsToExclude.includes(cell.column.id)
      ) {
        // only add data if column has no id OR column id is NOT in `columnIdsToExclude`
        dataRow[key] = cell.getValue();
      }
    });
    return dataRow;
  });

  const headers = _.map(_.keys(dataRows[0]), (headerKey) => {
    return {
      label: commonUtils.toTitleCase(headerKey),
      key: headerKey,
    };
  });

  return { dataRows, headers };
};

const RouteBase = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { reportingDate, dashboardMode, isPreview } = useDataMetadataContext();
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences(
    dashboardMode === 'preview'
      ? {
          tableKey: TABLE_KEY_PREVIEW,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_PREVIEW,
        }
      : {
          tableKey: TABLE_KEY_NON_PREVIEW,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_NON_PREVIEW,
        },
  );

  const [isLoading, setIsLoading] = useState(true);
  const [loanAccounts, setLoanAccounts] = useState<
    ValleyBankLoanAccountWithCollateralStats[]
  >([]);

  let routeLevelError = false;

  const { error } = useGetValleyBankUnderMarginLoanAccountsQuery({
    variables: {
      reportingDate,
      isPreview,
    },
    onCompleted: (data) => {
      if (data.valleyBankUnderMarginLoanAccounts) {
        try {
          const newLoanAccounts = parse.parseValleyBankLoanAccountWithCollateralStatsArray(
            data.valleyBankUnderMarginLoanAccounts,
          );
          const sortedLoanAccounts = newLoanAccounts.sort(
            (a, b) => a.marginAvailable - b.marginAvailable,
          );
          setLoanAccounts(sortedLoanAccounts);
        } catch (err) {
          Sentry.captureException(err);
        } finally {
          setIsLoading(false);
        }
      }
    },
    onError: () => {
      if (!routeLevelError && !toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load loan accounts under margin report page`;
        routeLevelError = true;
        toast(errorToastConfig);
        navigate('/');
      }
    },
  });

  // get pinned comments
  const [
    valleyBankLatestPinnedCommentForLoanAccounts,
    { data: latestPinnedCommentData, refetch: refetchLatestPinnedComment },
  ] = useGetValleyBankLatestPinnedCommentForLoanAccountsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onError: () => {
      if (!routeLevelError && !toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load loan accounts under margin report page`;
        routeLevelError = true;
        toast(errorToastConfig);
        navigate('/');
      }
    },
  });

  useEffect(() => {
    if (loanAccounts.length > 0 && !isLoading && dashboardMode !== 'preview') {
      valleyBankLatestPinnedCommentForLoanAccounts({
        variables: {
          loanAccountNums: loanAccounts.map(
            ({ loanAccountNumber }) => loanAccountNumber,
          ),
          reportingDate,
          isHistorical: dashboardMode === 'historical',
        },
      });
    }
  }, [
    isLoading,
    loanAccounts,
    reportingDate,
    dashboardMode,
    valleyBankLatestPinnedCommentForLoanAccounts,
  ]);

  const pinnedCommentMap = useMemo(() => {
    if (latestPinnedCommentData) {
      return _.keyBy(
        latestPinnedCommentData.valleyBankLatestPinnedCommentForLoanAccounts,
        (m) => m.loanAccountNum,
      );
    }
    return undefined;
  }, [latestPinnedCommentData]);

  const { columnDefs, columnNames } = useMemo(() => {
    if (dashboardMode === 'preview') {
      const _columnDefs = generateColumnDefs({
        dashboardMode: 'preview',
        pinnedCommentMap: undefined,
        handleDrawerClose: () => refetchLatestPinnedComment?.(),
      });

      return {
        columnDefs: _columnDefs,
        columnNames: getColumnNames(_columnDefs),
      };
    }
    const _columnDefs = generateColumnDefs({
      dashboardMode,
      pinnedCommentMap,
      handleDrawerClose: () => refetchLatestPinnedComment?.(),
    });
    return {
      columnDefs: _columnDefs,
      columnNames: getColumnNames(_columnDefs),
    };
  }, [dashboardMode, pinnedCommentMap, refetchLatestPinnedComment]);

  const table = useReactTable({
    data: loanAccounts,
    columns: columnDefs,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableDataForDownload = useMemo(
    () =>
      getUnderMarginReportDataForDownload({
        tableInstance: table,
        pinnedCommentMap,
      }),
    [table, pinnedCommentMap],
  );

  const totalUnderMargin = loanAccounts.reduce(
    (acc, { marginAvailable }) => acc + marginAvailable,
    0,
  );

  const totalLoanAccountsUnderMargin = loanAccounts.length;

  const averageUnderMargin = commonUtils.divideByOrZero(
    totalUnderMargin,
    totalLoanAccountsUnderMargin,
  );

  return (
    <>
      <LoanAccountsOverviewTemplate
        selectedTab="underMarginReport"
        headerChildren={
          <ValleyBankLoanAccountEventLogMultiEntityDrawer
            onThreadAction={refetchLatestPinnedComment}
          />
        }
      >
        {isLoading && (
          <ProgressAlert>Loading under margin report</ProgressAlert>
        )}
        {!isLoading && loanAccounts.length === 0 && (
          <Alert status="info">
            <Text>No loan accounts under margin found</Text>
          </Alert>
        )}
        {!isLoading && loanAccounts.length > 0 && (
          <>
            <HStack w="100%" spacing="24px" p="40px 0">
              <InfoCard
                title="Total Under Margin"
                value={formatCurrencyAmount(
                  totalUnderMargin,
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" />}
                flexGrow={1}
              />
              <InfoCard
                title="Number of Loan Accounts Under Margin"
                value={totalLoanAccountsUnderMargin.toString()}
                icon={<Icon icon="hashtag" />}
                flexGrow={1}
              />
              <InfoCard
                title="Average Under Margin"
                value={formatCurrencyAmount(
                  averageUnderMargin,
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" />}
                flexGrow={1}
              />
            </HStack>
            <HStack w="100%" justify="flex-end">
              <ColumnDisplaySettingsButton
                initialColumns={columnConfiguration}
                updateColumnConfigs={updateUserColumnConfiguration}
                columnNames={columnNames}
              />
              <CsvXlsxDownloadSelect
                dataRows={tableDataForDownload.dataRows}
                headers={tableDataForDownload.headers}
                filenamePrefix="loan-accounts-under-margin-report"
              />
            </HStack>
            <LoanAccountsTableContainer
              table={table}
              isLoading={isLoading}
              isError={Boolean(error)}
            />
            <TanStackPaginationControl table={table} />
          </>
        )}
      </LoanAccountsOverviewTemplate>
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="../" />;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
